// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SubscriberCb<T = any> = (
  key: string,
  err: Error | StoreError | null,
  data?: Timestamped<T> | undefined,
) => void

export type Timestamped<T> = { data: T; ts: number }

/**
 * Custom error type for passing errors over the wire. The structured clone
 * algorithm (which is used for passing data over the SharedWorker message bus)
 * doesn't properly clone Error objects unless they conform to a very specific
 * format. In particular this means that custom error properties are not
 * serialized, and so we can't use them for passing context data back to the
 * caller (at least not without just parsing the `message` string). This type
 * gives us some control if we want to serialise context.
 */
export type StoreError =
  | { reason: 'subscriber_destroyed'; subscriberId: string }
  | { reason: 'force_resubscribed'; key: string; ts: number }
export function isStoreError(obj: unknown): obj is StoreError {
  return typeof obj === 'object' && obj !== null && 'reason' in obj
}

type ResponseStatus = 'succeeded' | 'errored' | 'aborted'

export class Store<T> {
  private state: {
    [key: string]: {
      url: string
      subscribers: {
        [id: string]: {
          lastPingTime: number

          cb: SubscriberCb<T>
        }
      }
      /** Cached data so we can deliver it immediately to new subscribers */
      data: Timestamped<T> | undefined
      delay: number
      /**
       * Registered recursive setTimeout that polls according to delay, stored
       * here so we can clear it if needed
       */
      timer?: ReturnType<typeof setTimeout>
      abortController: AbortController
    }
  }

  // We store the gc setInterval in a private variable. We're not currently
  // using this for anything yet.
  private gcInterval: ReturnType<typeof setTimeout>

  constructor() {
    this.state = {}
    this.gcInterval = setInterval(() => this.gc(), 4000)
  }

  /**
   * Subscribe a client to a query. This function is idempotent, and is also the
   * means by which already-connected clients update their last ping time.
   */
  public subscribe(
    id: string,
    key: string,
    url: string,
    delay: number,
    cb: SubscriberCb,
  ): void {
    // If there's an existing query for this key then we don't need to recreate
    // the query from scratch
    if (this.state[key]) {
      // return cached data to new subscribers immediately
      if (this.state[key].data && !this.state[key].subscribers[id]) {
        cb(key, null, this.state[key].data)
      }

      // If the query still exists, but has been aborted, that means that all
      // queries have unsubscribed but the query has not yet been garbage
      // collected. This means that there's no future polling scheduled, so we
      // need to start it up again.
      if (this.state[key].abortController.signal.aborted) {
        this.state[key].abortController = new AbortController()
        this.state[key].delay = delay
        this.poll(key, url)

        // Otherwise, if the new subscriber has a more frequent delay than the
        // existing poll interval, update to the new frequency.
        //
        // Note that this is very likely an edge case as most queries are only
        // defined once, so won't have different delays. We're also
        // intentionally not handling the case of going from a shorter delay up
        // to a longer delay, as it doesn't seem like the additional complexity
        // is worth it for something that's even more of an edge case.
      } else if (this.state[key].delay > delay) {
        this.state[key].delay = delay
        this.poll(key, url)
      }

      // update the last ping time and callback on every subscription request
      this.state[key].subscribers[id] = {
        lastPingTime: Date.now(),
        cb,
      }
    } else {
      // There's no query so create it from scratch
      this.state[key] = {
        url,
        subscribers: { [id]: { lastPingTime: Date.now(), cb } },
        data: undefined,
        delay,
        abortController: new AbortController(),
      }
      this.poll(key, url)
    }
  }

  /** Unsubscribe a client from a query */
  public unsubscribe(id: string, key: string): void {
    if (!this.state[key]) return
    delete this.state[key].subscribers[id]
    // If there are no more clients, stop polling for this query. Query data
    // will remain in the cache until the garbage collector gets rid of it. We
    // do this so that if the UI does a quick unsub/sub for the same query (e.g.
    // a route transition between pages that use the same query), we'll have
    // cached data and can display the new UI immediately.
    if (Object.entries(this.state[key].subscribers).length === 0) {
      this.state[key].abortController.abort()
      clearTimeout(this.state[key].timer)
      delete this.state[key].timer
    }
  }

  /**
   * Drop the key from the store, which should force all connected consumers to
   * re-subscribe. This also broadcasts the fact of the resubscription to other
   * consumers, so they don't all keep trying to resubscribe.
   */
  public forceResubscribe(key: string): void {
    this.notify(key, {
      reason: 'force_resubscribed',
      key,
      ts: Date.now(),
    })
    this.unmount(key)
  }

  /** Dumps the current state of the store to the caller */
  public dump(): typeof this.state {
    return JSON.parse(JSON.stringify(this.state))
  }

  /** Destroy the store */
  public destroy(): void {
    // Let subscribers know they're being destroyed so they can stop subscribing
    for (const key in this.state) {
      for (const subscriberId in this.state[key].subscribers) {
        this.state[key].subscribers[subscriberId].cb(key, {
          reason: 'subscriber_destroyed',
          subscriberId,
        })
      }
      this.unmount(key)
    }
  }

  /** Run a query against the API */
  private async fetchData(key: string, url: string): Promise<ResponseStatus> {
    // It's important that this function never throws an error, so wrap it in a
    // try/catch block and ensure that errors are caught and result in a proper
    // return value
    try {
      const res = await fetch(url, {
        credentials: 'include',
        signal: this.state[key].abortController.signal,
      })
      if (res.ok) {
        const data: T = await res.json()
        const wrappedData = { data, ts: Date.now() }
        this.state[key].data = wrappedData
        this.notify(key, null, wrappedData)
        return 'succeeded'
      } else {
        throw Error(`${res.status} ${res.statusText}`)
      }
    } catch (err) {
      if ((err as DOMException).name === 'AbortError') {
        return 'aborted'
      }
      this.notify(key, err as Error)
      return 'errored'
    }
  }

  private async poll(key: string, url: string) {
    // Wrap this in a try/catch block so that we never have any uncaught errors.
    // Otherwise we won't queue up the next iteration of the polling loop, and
    // polling will stall. In practice, fetchData is the place that we'd get
    // errors, and that's wrapped in a try/catch block. However, out of an
    // abundance of caution, ensure that any errors that might occur in the body
    // of this function don't lead to stalled polling.
    try {
      if (!this.state[key]) {
        return
      }
      // Unconditionally clear any polling that might already be queued for this
      // key. This avoids us accidentally getting into weird states where we
      // have multiple versions of the same query running
      clearTimeout(this.state[key].timer)
      const start = Date.now()
      const res = await this.fetchData(key, url)
      const delay = this.state[key].delay
      let nextPollDelay = delay
      const latency = Date.now() - start
      switch (res) {
        case 'succeeded':
          nextPollDelay = Math.max(delay - latency, 0)
          break
        case 'errored':
          // retry with 5s backoff
          nextPollDelay = 5000
          break
        case 'aborted':
          // Aborts arise from clients unsubscribing, so we don't need to queue up
          // the next poll
          return
      }

      // Check that this.state[key] still exists. If not, the query may have
      // been garbage collected. We ran this check before, but garbage
      // collection may have happened while the query was running.
      if (!this.state[key]) {
        return
      }
      this.state[key].timer = setTimeout(
        () => this.poll(key, url),
        nextPollDelay,
      )
    } catch (err) {
      // This is a bad case, and we should never get here assuming that
      // fetchData's error handling has done its job properly. However, out of
      // an abundance of caution, make sure that polling never stalls even if we
      // were to get an uncaught error.
      this.notify(key, err as Error)
      // Unmount the subscription entirely. If the a calling component for this
      // query is still active, it'll resubscribe again momentarily.
      this.unmount(key)
    }
  }

  /** Notify all subscribers of new data via callback */
  private notify(
    key: string,
    err: Error | StoreError | null,
    data?: Timestamped<T>,
  ) {
    for (const id in this.state[key]?.subscribers) {
      this.state[key].subscribers[id].cb(key, err, data)
    }
  }

  /** Completely remove a running query from the state */
  private unmount(key: string) {
    if (!this.state[key]) {
      return
    }
    this.state[key].abortController.abort()
    clearTimeout(this.state[key].timer)
    delete this.state[key]
  }

  /**
   * Garbage collection function, removes queries that have no active
   * subscribers
   */
  private gc() {
    const threshold = Date.now() - 15000
    for (const key in this.state) {
      for (const id in this.state[key].subscribers) {
        if (this.state[key].subscribers[id].lastPingTime < threshold) {
          this.state[key].subscribers[id].cb(
            key,
            new Error(`Query subscription timed out`),
          )
          delete this.state[key].subscribers[id]
        }
      }
      if (Object.entries(this.state[key].subscribers).length === 0) {
        this.unmount(key)
      }
    }
  }
}

export type PublicStore<T> = Pick<
  Store<T>,
  'subscribe' | 'unsubscribe' | 'dump' | 'forceResubscribe'
>
